'use client';

import React, { useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import en from '@/../public/img/lang/en.svg';
import fa from '@/../public/img/lang/fa.svg';
import fr from '@/../public/img/lang/fr.svg';
import de from '@/../public/img/lang/icon/DE.png';
import es from '@/../public/img/lang/icon/ES.png';
import ar from '@/../public/img/lang/icon/IQ.png';
import ckb from '@/../public/img/lang/icon/KU.png';
import tl from '@/../public/img/lang/icon/PH.png';
import pl from '@/../public/img/lang/icon/PL.png';
import pt from '@/../public/img/lang/icon/PT.png';
import sq from '@/../public/img/lang/icon/SQ.png';
import tr from '@/../public/img/lang/icon/TR.png';
import uk from '@/../public/img/lang/icon/UA.png';
import uz from '@/../public/img/lang/icon/UZ.png';
import ru from '@/../public/img/lang/ru.svg';
import { languages } from '@/app/constants';
import {
  Card,
  CardContent,
  MenuItem,
  Select,
  SelectChangeEvent,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';

import { useMediaQuery } from '@/hooks/use-media-query';

const languageImages: Record<string, { src: string }> = {
  en,
  fr,
  fa,
  ru,
  es,
  ar,
  pl,
  de,
  ckb,
  tr,
  tl,
  pt,
  uk,
  uz,
  sq,
};

interface MenuItem {
  label: string;
  value: string;
}

interface Language {
  label: string;
}
type Languages = Record<string, Language>;

export function LanguageSelector(): React.JSX.Element {
  const searchParams = useSearchParams();
  const router = useRouter();
  const mdDown = useMediaQuery('down', 'md');
  const [selectedValue, setSelectedValue] = useState<string>('en');
  const selectedOpId = searchParams.get('opId');
  const [availableLanguages, setAvailableLanguages] = useState<MenuItem[]>([]);

  const getAvailableLanguages = async (): Promise<void> => {
    try {
      const res = await fetch(`/api/cms/languages?opId=${selectedOpId}`);
      if (res?.ok) {
        const data = await res.json();
        if (data?.success) {
          const allLanguages: Languages = languages;

          const mappedArray: MenuItem[] = (data.data as string[]).map((code) => ({
            label: allLanguages[code]?.label ?? 'Unknown',
            value: code,
          }));

          const currentLanguage = searchParams.get('selectedLanguage');
          if (currentLanguage) {
            setSelectedValue(currentLanguage);
          }

          setAvailableLanguages(mappedArray);
        }
      }
    } catch (error) {
      //  empty block
    }
  };

  const updateLanguage = (selectedLang: string): void => {
    if (selectedLang) {
      const currentSearchParams = new URLSearchParams(window.location.search);
      currentSearchParams.set('selectedLanguage', selectedLang);

      // Preserve existing filter parameters
      ['limit', 'opId', 'includeBrands'].forEach((param) => {
        const value = searchParams.get(param);
        if (value) currentSearchParams.set(param, value);
      });

      // Update URL
      const currentUrl = new URL(window.location.href);
      currentUrl.search = currentSearchParams.toString();
      router.replace(currentUrl.toString());

      setSelectedValue(selectedLang);
    }
  };

  // Handler for ToggleButtonGroup
  const handleToggleChange = (event: React.MouseEvent<HTMLElement>, newValue: string): void => {
    if (newValue) {
      updateLanguage(newValue);
    }
  };

  // Handler for Select component
  const handleSelectChange = (event: SelectChangeEvent): void => {
    updateLanguage(event.target.value);
  };

  useEffect(() => {
    void getAvailableLanguages();
  }, [selectedOpId]);

  if (!availableLanguages.length) return <></>;

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        {mdDown ? (
          <Box>
            <Typography variant="body1">Select Language:</Typography>
            <Select
              value={selectedValue}
              onChange={handleSelectChange}
              size="small"
              sx={{ minWidth: 120, width: 1, mt: 1 }}
              displayEmpty
              MenuProps={{
                slotProps: {
                  paper: {
                    style: {
                      maxHeight: 200,
                    },
                  },
                },
              }}
            >
              {availableLanguages.map((lang) => (
                <MenuItem key={lang.value} value={lang.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <img
                      src={languageImages[lang.value]?.src || ''}
                      style={{ width: '20px', height: '20px' }}
                      alt={lang.label}
                    />
                    <Typography variant="overline">{lang.label}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </Box>
        ) : (
          <Stack gap={1}>
            <Typography variant="body1">Select Language:</Typography>
            <ToggleButtonGroup
              value={selectedValue}
              onChange={handleToggleChange}
              size="small"
              exclusive
              sx={{ flexWrap: 'wrap' }}
            >
              {availableLanguages.map((lang) => (
                <ToggleButton key={lang.value} value={lang.value} sx={{ fontSize: 14 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                    <img
                      src={languageImages[lang.value]?.src || en.src}
                      style={{ width: '20px', height: '20px' }}
                      alt="language"
                    />
                    <Typography variant="overline">{lang.label}</Typography>
                  </Box>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
